/**
 * "Share this link" — how a phone reader gets a page onto their desktop
 * (ROADMAP "The phone branch", D2). The native share sheet where the phone has
 * one, so the reader sends it to their own mail or notes; a clipboard copy
 * otherwise. No backend: an anonymous "email me the link" would be an open
 * mail relay, and is a second-pass item.
 */
import React, { useCallback, useState } from 'react';
import { Button, Snackbar } from '@mui/material';
import { IosShare } from '@mui/icons-material';
import analyticsService from '../services/analytics';

type Outcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** Share `url`, falling back to the clipboard. Never throws. */
export async function shareOrCopy(url: string, title: string): Promise<Outcome> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url, title });
      return 'shared';
    } catch (err) {
      // Closing the sheet rejects with AbortError — the reader's choice, not a failure.
      if ((err as { name?: string })?.name === 'AbortError') return 'cancelled';
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

const MESSAGES: Partial<Record<Outcome, string>> = {
  copied: 'Link copied',
  failed: 'Could not copy the link — copy it from the address bar',
};

const ShareLinkButton: React.FC<{ source: string; variant?: 'contained' | 'outlined' }> = ({
  source,
  variant = 'contained',
}) => {
  const [message, setMessage] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    const outcome = await shareOrCopy(window.location.href, document.title);
    analyticsService.logEvent('phone_share_link', { source, outcome });
    setMessage(MESSAGES[outcome] ?? null);
  }, [source]);

  return (
    <>
      <Button
        variant={variant}
        startIcon={<IosShare />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          ...(variant === 'contained'
            ? { bgcolor: 'text.primary', color: 'background.paper', '&:hover': { bgcolor: 'text.secondary' } }
            : { color: 'text.primary', borderColor: 'divider' }),
        }}
      >
        Share this link
      </Button>
      <Snackbar
        open={message !== null}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Clear the phone's bottom tab bar rather than sit on it.
        sx={{ bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
      />
    </>
  );
};

export default ShareLinkButton;
